import { tracking } from '@iwoca/frontend-tracking-library';
import { RootState } from '@iwProvider/configureStore';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import CheckMarkBulletPoint from './CheckMarkBulletPoint';
import styles from './TopUpEligibilitySection.module.css';
import { REQUEST_CHANGE_ROUTER } from '../../../iwRouter/IwRouter.actions';
import ConfirmationButton from '../../ui/confirmationButton/ConfirmationButton';
import { getAllAccounts } from '../accountsSelector';

const mapStateToProps = (state: RootState) => {
    const endedLoan = _.find(getAllAccounts(state), {
        loan_status: 'ended'
    });

    return {
        accountId: endedLoan?.account_id
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    requestTopUp: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: '/apply-flexi-loan/' }
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const TopUpEligibilitySection = ({
    requestTopUp,
    accountId
}: PropsFromRedux) => {
    const handleRequestTopUp = () => {
        tracking.ampli.newLoanRequestStarted({
            account_id: accountId
        });
        requestTopUp();
    };

    return (
        <div className={styles.HighlightSection}>
            <h2 className={styles.HighlightSectionTitle}>
                <FormattedMessage
                    id="_.outstanding_view.topup_eligibility.good_news"
                    defaultMessage="Is it time for another Flexi-Loan?"
                />
            </h2>
            <CheckMarkBulletPoint
                title={
                    <FormattedMessage
                        id="_.outstanding_view.topup_eligibility.bullet_point_one_title"
                        defaultMessage="What you need to fast track your business"
                    />
                }
                description={
                    <FormattedMessage
                        id="_.outstanding_view.topup_eligibility.bullet_point_one_description"
                        defaultMessage="Stock, bills, some new equipment, or perhaps just some working capital to keep things flowing."
                    />
                }
            />

            <CheckMarkBulletPoint
                title={
                    <FormattedMessage
                        id="_.outstanding_view.topup_eligibility.bullet_point_two_title"
                        defaultMessage="Easy application and a quick decision"
                    />
                }
                description={
                    <FormattedMessage
                        id="_.outstanding_view.topup_eligibility.bullet_point_two_description"
                        defaultMessage="We might only need a few documents, depending on what you've applied for, and we'll be able to give you a decision within one working day."
                    />
                }
            />

            <CheckMarkBulletPoint
                title={
                    <FormattedMessage
                        id="_.outstanding_view.topup_eligibility.bullet_point_three_title"
                        defaultMessage="Use it on top of a Bounce Back Loan"
                    />
                }
                description={
                    /* eslint-disable formatjs/enforce-placeholders -- dePhoneNumber used in DE translation */
                    <FormattedMessage
                        id="_.outstanding_view.topup_eligibility.bullet_point_three_description"
                        defaultMessage="You can still apply for a Flexi-Loan if you already have a {bblLink}, and you won't need to repay the BBL if you take another Flexi-Loan with us."
                        values={{
                            dePhoneNumber: (
                                <a href="tel:+496994323020">069 / 94323020</a>
                            ),
                            bblLink: (
                                <a
                                    href="https://www.iwoca.co.uk/insights/coronavirus-bounce-back-loan/"
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    BBL
                                </a>
                            )
                        }}
                    />
                    /* eslint-enable formatjs/enforce-placeholders */
                }
            />
            <ConfirmationButton
                className={styles.callToAction}
                onClick={handleRequestTopUp}
            >
                <FormattedMessage
                    id="_.outstanding_view.topup_eligibility.apply_now"
                    defaultMessage="Apply now"
                />
            </ConfirmationButton>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TopUpEligibilitySection);
